import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import toast from 'react-hot-toast';
import { Plus, Trash2, Pencil, Check, X, GripVertical, ChevronDown, ChevronRight } from 'lucide-react';

interface Klavzula {
  id: number;
  predal: string;
  naziv: string;
  kljucne_besede: string;
  cleni: string;
  aktiven: boolean;
  vrstni_red: number;
}

const PREDALI = [
  { key: 'oproscen', naziv: 'Oproščen promet', opis: 'Promet, oproščen plačila DDV — npr. najem, zavarovanja, izvoz', rob: 'border-l-emerald-400 dark:border-l-emerald-600', dot: 'bg-emerald-500' },
  { key: 'ni_predmet', naziv: 'Ni predmet DDV', opis: 'Ni predmet obdavčitve — npr. zamudne obresti, odškodnine', rob: 'border-l-sky-400 dark:border-l-sky-600', dot: 'bg-sky-500' },
  { key: 'se_ne_odbija', naziv: 'Se ne odbija', opis: 'DDV se ne sme odbiti, gre v strošek — npr. reprezentanca, gorivo', rob: 'border-l-amber-400 dark:border-l-amber-600', dot: 'bg-amber-500' },
  { key: 'samoobdavcitev', naziv: 'Samoobdavčitev', opis: 'DDV obračuna prejemnik (obrnjena davčna obveznost)', rob: 'border-l-violet-400 dark:border-l-violet-600', dot: 'bg-violet-500' },
  { key: 'eu', naziv: 'EU samoobdavčitev', opis: 'Pridobitve blaga in storitev iz EU', rob: 'border-l-indigo-400 dark:border-l-indigo-600', dot: 'bg-indigo-500' },
];

// Uporabniku prijazna referenca iz clenov. Besede (kljucne_besede) so interni motor
// ujemanja in se uporabniku NE prikazujejo - urejajo se le v obrazcu za dodajanje/urejanje.
const referenca = (cleni: string) => {
  const c = (cleni || '').split('|').map((s) => s.trim()).filter(Boolean);
  if (c.length === 0) return '';
  const fmt = c.map((n) => (/^\d+$/.test(n) ? n + '.' : n));
  return 'po ZDDV-1, ' + fmt.join(', ') + ' člen';
};

export function DdvKlavzule() {
  const [klavzule, setKlavzule] = useState<Klavzula[]>([]);
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [editVal, setEditVal] = useState({ naziv: '', kljucne_besede: '', cleni: '' });
  const [novaZa, setNovaZa] = useState<string | null>(null);
  const [nova, setNova] = useState({ naziv: '', kljucne_besede: '', cleni: '' });
  const [zaprto, setZaprto] = useState<Record<string, boolean>>({});

  const nalozi = () => {
    setLoading(true);
    api.get('/vat-clauses')
      .then((r) => setKlavzule(r.data))
      .catch(() => toast.error('Napaka pri nalaganju klavzul'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { nalozi(); }, []);

  const poPredalu = (p: string) =>
    klavzule.filter((k) => k.predal === p).sort((a, b) => (a.vrstni_red - b.vrstni_red) || (a.id - b.id));

  const onDrop = async (predal: string) => {
    const id = dragId;
    setDragId(null);
    setDragOver(null);
    if (id == null) return;
    const k = klavzule.find((x) => x.id === id);
    if (!k || k.predal === predal) return;
    setKlavzule((prev) => prev.map((x) => (x.id === id ? { ...x, predal } : x)));
    try { await api.put(`/vat-clauses/${id}`, { predal }); }
    catch { toast.error('Premik ni uspel'); nalozi(); }
  };

  const toggle = async (k: Klavzula) => {
    setKlavzule((prev) => prev.map((x) => (x.id === k.id ? { ...x, aktiven: !x.aktiven } : x)));
    try { await api.put(`/vat-clauses/${k.id}`, { aktiven: !k.aktiven }); }
    catch { toast.error('Sprememba ni uspela'); nalozi(); }
  };

  const izbrisi = async (k: Klavzula) => {
    if (!window.confirm(`Izbrišem klavzulo "${k.naziv}"?`)) return;
    setKlavzule((prev) => prev.filter((x) => x.id !== k.id));
    try { await api.delete(`/vat-clauses/${k.id}`); }
    catch { toast.error('Brisanje ni uspelo'); nalozi(); }
  };

  const zacniUredi = (k: Klavzula) => {
    setEditId(k.id);
    setEditVal({ naziv: k.naziv, kljucne_besede: k.kljucne_besede || '', cleni: k.cleni || '' });
  };
  const shraniUredi = async (id: number) => {
    if (!editVal.naziv.trim()) { toast.error('Naziv je obvezen'); return; }
    const v = { ...editVal };
    setKlavzule((prev) => prev.map((x) => (x.id === id ? { ...x, ...v } : x)));
    setEditId(null);
    try { await api.put(`/vat-clauses/${id}`, v); }
    catch { toast.error('Shranjevanje ni uspelo'); nalozi(); }
  };

  const dodaj = async (predal: string) => {
    if (!nova.naziv.trim()) { toast.error('Naziv je obvezen'); return; }
    try {
      const r = await api.post('/vat-clauses', { predal, ...nova });
      setKlavzule((prev) => [...prev, r.data]);
      setNova({ naziv: '', kljucne_besede: '', cleni: '' });
      setNovaZa(null);
    } catch { toast.error('Dodajanje ni uspelo'); }
  };

  const inputCls = 'w-full px-2.5 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white';

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 space-y-4">
      <div>
        <h2 className="font-semibold text-gray-900 dark:text-white">📑 DDV klavzule na računih</h2>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Ko OCR v besedilu računa najde besede ali člene klavzule, ga razvrsti v izbrani predal. Povleci vrstico med predali, stikalo jo vklopi/izklopi. Katalog je skupen za vse baze.
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-gray-400 py-6 text-center">Nalagam…</div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 items-start">
          {/* PATCH_KLAVZULE_2COL_V1: levo oproscen+se_ne_odbija, desno ni_predmet+samoobdavcitev+eu */}
          {[['oproscen', 'se_ne_odbija'], ['ni_predmet', 'samoobdavcitev', 'eu']].map((stolpec, si) => (
          <div key={si} className="space-y-3">
          {PREDALI.filter((p) => stolpec.includes(p.key)).map(({ key, naziv, opis, rob, dot }) => {
            const items = poPredalu(key);
            const over = dragOver === key;
            const collapsed = !!zaprto[key];
            return (
              <div
                key={key}
                onDragOver={(e) => { e.preventDefault(); setDragOver(key); }}
                onDragLeave={() => setDragOver((d) => (d === key ? null : d))}
                onDrop={() => onDrop(key)}
                className={`rounded-xl border border-l-4 ${rob} ${
                  over ? 'border-gray-400 dark:border-gray-500 bg-gray-50 dark:bg-gray-800/40' : 'border-gray-200 dark:border-gray-700'
                } transition-colors`}
              >
                {/* glava predala */}
                <button
                  onClick={() => setZaprto((z) => ({ ...z, [key]: !z[key] }))}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-left"
                >
                  {collapsed ? <ChevronRight className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  <span className={`h-2.5 w-2.5 rounded-full ${dot}`} />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">{naziv}</div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400">{opis}</div>
                  </div>
                  <span className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-full px-2 py-0.5">{items.length}</span>
                </button>

                {!collapsed && (
                  <div className="px-3 pb-3 space-y-2">
                    {items.length === 0 && novaZa !== key && (
                      <div className="text-xs text-gray-400 dark:text-gray-500 px-2 py-1">Ni klavzul v tem predalu.</div>
                    )}

                    {items.map((k) => (
                      <div
                        key={k.id}
                        draggable={editId !== k.id}
                        onDragStart={() => setDragId(k.id)}
                        onDragEnd={() => { setDragId(null); setDragOver(null); }}
                        className={`rounded-xl border bg-white dark:bg-gray-900 ${
                          k.aktiven ? 'border-gray-200 dark:border-gray-700' : 'border-gray-200 dark:border-gray-800 opacity-60'
                        } ${dragId === k.id ? 'ring-2 ring-blue-400' : ''}`}
                      >
                        {editId === k.id ? (
                          <div className="p-3 space-y-2">
                            <input value={editVal.naziv} onChange={(e) => setEditVal({ ...editVal, naziv: e.target.value })} placeholder="Naziv klavzule" className={inputCls} />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <input value={editVal.kljucne_besede} onChange={(e) => setEditVal({ ...editVal, kljucne_besede: e.target.value })} placeholder="Ključne besede za prepoznavo (ločene z |)" className={inputCls} />
                              <input value={editVal.cleni} onChange={(e) => setEditVal({ ...editVal, cleni: e.target.value })} placeholder="Členi ZDDV-1 (npr. 44)" className={inputCls} />
                            </div>
                            <div className="flex gap-2 justify-end">
                              <button onClick={() => shraniUredi(k.id)} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-emerald-500 text-white"><Check className="w-3.5 h-3.5" /> Shrani</button>
                              <button onClick={() => setEditId(null)} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200"><X className="w-3.5 h-3.5" /> Prekliči</button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3 p-3">
                            <GripVertical className="w-4 h-4 text-gray-300 dark:text-gray-600 shrink-0 cursor-grab" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">{k.naziv}</div>
                              {referenca(k.cleni) && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">{referenca(k.cleni)}</div>
                              )}
                            </div>
                            <button onClick={() => toggle(k)} title={k.aktiven ? 'Izklopi' : 'Vklopi'}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full shrink-0 transition-colors ${k.aktiven ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'}`}>
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${k.aktiven ? 'translate-x-6' : 'translate-x-1'}`} />
                            </button>
                            <button onClick={() => zacniUredi(k)} className="p-1.5 text-gray-400 hover:text-blue-500 shrink-0" title="Uredi"><Pencil className="w-4 h-4" /></button>
                            <button onClick={() => izbrisi(k)} className="p-1.5 text-gray-400 hover:text-red-500 shrink-0" title="Izbriši"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        )}
                      </div>
                    ))}

                    {novaZa === key ? (
                      <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 p-3 space-y-2">
                        <input value={nova.naziv} onChange={(e) => setNova({ ...nova, naziv: e.target.value })} placeholder="Naziv klavzule" className={inputCls} />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <input value={nova.kljucne_besede} onChange={(e) => setNova({ ...nova, kljucne_besede: e.target.value })} placeholder="Ključne besede za prepoznavo (ločene z |)" className={inputCls} />
                          <input value={nova.cleni} onChange={(e) => setNova({ ...nova, cleni: e.target.value })} placeholder="Členi ZDDV-1 (npr. 44)" className={inputCls} />
                        </div>
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => dodaj(key)} className="px-3 py-1.5 text-xs rounded-lg bg-emerald-500 text-white">Dodaj</button>
                          <button onClick={() => { setNovaZa(null); setNova({ naziv: '', kljucne_besede: '', cleni: '' }); }} className="px-3 py-1.5 text-xs rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200">Prekliči</button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => setNovaZa(key)} className="w-full flex items-center justify-center gap-1 py-2 text-xs text-gray-500 dark:text-gray-400 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 hover:border-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
                        <Plus className="w-4 h-4" /> Dodaj klavzulo
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          </div>
          ))}
        </div>
      )}
    </div>
  );
}
